/**
 * OpenStreetMap's railings and parapets on bridges (`LINE_RAISED` barriers),
 * for the bridge they stand on: where one runs along a deck's edge, the deck
 * is widened out to it (`bridges.ts`) and the game's own parapet, edge beam
 * and guardrail are left out on that side (`guardrails.ts`). The mapped one
 * stands there instead (`osmlines.ts`).
 *
 * Pure: no fetch, no DOM.
 */

import type { OsmLineData } from './osmlines.js';
import { LINE_RAISED } from './osmlines.js';

/** How far out from a deck's edge a mapped railing is still its, metres. */
export const RAILING_REACH = 8;

const CELL = 16;
const key = (cx: number, cz: number) => cx * 73856093 ^ cz * 19349663;

export class RailingIndex {
  /** x0, z0, x1, z1 per segment. */
  private readonly segs: number[] = [];
  private readonly cells = new Map<number, number[]>();

  constructor(data: OsmLineData | undefined) {
    if (data === undefined || data.flags === undefined) return;
    for (let li = 0; li < data.kinds.length; li++) {
      if ((data.flags[li] & LINE_RAISED) === 0) continue;
      const start = data.ranges[li * 2], count = data.ranges[li * 2 + 1];
      for (let k = 0; k + 1 < count; k++) {
        const i = (start + k) * 2;
        const x0 = data.points[i], z0 = data.points[i + 1], x1 = data.points[i + 2], z1 = data.points[i + 3];
        const s = this.segs.length / 4;
        this.segs.push(x0, z0, x1, z1);
        for (let cx = Math.floor(Math.min(x0, x1) / CELL); cx <= Math.floor(Math.max(x0, x1) / CELL); cx++) {
          for (let cz = Math.floor(Math.min(z0, z1) / CELL); cz <= Math.floor(Math.max(z0, z1) / CELL); cz++) {
            const k2 = key(cx, cz);
            const list = this.cells.get(k2);
            if (list === undefined) this.cells.set(k2, [s]); else list.push(s);
          }
        }
      }
    }
  }

  get empty(): boolean {
    return this.segs.length === 0;
  }

  /**
   * How far out from (x, z) along the unit direction (ox, oz) the first
   * mapped railing is, within `reach` metres; undefined where there is none.
   * Only one running roughly along the edge counts, not one crossing it.
   */
  outward(x: number, z: number, ox: number, oz: number, reach = RAILING_REACH): number | undefined {
    if (this.segs.length === 0) return undefined;
    const ex = x + ox * reach, ez = z + oz * reach;
    let best: number | undefined;
    const seen = new Set<number>();
    for (let cx = Math.floor(Math.min(x, ex) / CELL); cx <= Math.floor(Math.max(x, ex) / CELL); cx++) {
      for (let cz = Math.floor(Math.min(z, ez) / CELL); cz <= Math.floor(Math.max(z, ez) / CELL); cz++) {
        for (const s of this.cells.get(key(cx, cz)) ?? []) {
          if (seen.has(s)) continue;
          seen.add(s);
          const ax = this.segs[s * 4], az = this.segs[s * 4 + 1], bx = this.segs[s * 4 + 2], bz = this.segs[s * 4 + 3];
          const dx = bx - ax, dz = bz - az, len = Math.hypot(dx, dz);
          if (len < 1e-6) continue;
          // Along the edge: across the ray, not along it.
          if (Math.abs((dx * ox + dz * oz) / len) > 0.5) continue;
          // Ray (x, z) + t (ox, oz) against the segment a + u (b - a).
          const den = ox * dz - oz * dx;
          if (Math.abs(den) < 1e-9) continue;
          const t = ((ax - x) * dz - (az - z) * dx) / den;
          const u = ((ax - x) * oz - (az - z) * ox) / den;
          if (t < 0 || t > reach || u < 0 || u > 1) continue;
          if (best === undefined || t < best) best = t;
        }
      }
    }
    return best;
  }
}
